import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { StudentTutorSortPreset } from 'hooks/useStudentTutorSearch';

type StudentTutorSortContextValue = {
  sortPreset: StudentTutorSortPreset;
  setSortPreset: (preset: StudentTutorSortPreset) => void;
  resetSort: () => void;
};

const StudentTutorSortContext = createContext<StudentTutorSortContextValue | null>(
  null,
);

export const StudentTutorSortProvider = ({ children }: { children: ReactNode }) => {
  const [sortPreset, setSortPreset] = useState<StudentTutorSortPreset>(null);

  const value = useMemo(
    () => ({
      sortPreset,
      setSortPreset,
      resetSort: () => setSortPreset(null),
    }),
    [sortPreset],
  );

  return (
    <StudentTutorSortContext.Provider value={value}>
      {children}
    </StudentTutorSortContext.Provider>
  );
};

export const useStudentTutorSort = (): StudentTutorSortContextValue => {
  const ctx = useContext(StudentTutorSortContext);
  if (!ctx) {
    throw new Error('useStudentTutorSort must be used within StudentTutorSortProvider');
  }
  return ctx;
};

/** Safe for DetailsMenu on non-search routes where provider is absent. */
export const useStudentTutorSortOptional = (): StudentTutorSortContextValue | null =>
  useContext(StudentTutorSortContext);
